import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { Link, withRouter } from "react-router-dom";
import { TRAINER_CATEGORY_TABS } from "../Trainers/trainerCategoryTabs";
import { sendPracticeEvent, sendTreadmillEvent, treadmillAuthSegmentForGa } from "../../utils/analytics";
import Signup from "../Auth/Signup";
import {
  startFacebookLogin,
  startGoogleLogin,
  signupEmailAndPasswordLogin,
  setProfileName,
} from "../../store/actions/authActions";
import {
  TREADMILL_TRAINER_HAND_SHAPE,
  TREADMILL_TRAINER_OPPONENT_SHAPE,
  TREADMILL_TRAINER_BUILDING_BLOCKS,
  TREADMILL_TRAINER_CARD_RUSH,
  incrementTreadmillDailyCount,
  treadmillDailyLimitReached,
  treadmillFreeRoundsRemaining,
} from "../../utils/treadmillDailyTries";
import HandShapeMissingClubTrainer from "./HandShapeMissingClubTrainer";
import OpponentShapeTrainer from "./OpponentShapeTrainer";
import BuildingBlocksTrainer from "./BuildingBlocksTrainer";
import CardRushTrainer from "./CardRushTrainer";
import CountingHandsTrainer from "../Counting/CountingHandsTrainer";
import BridgeSudokuTrainer from "./BridgeSudokuTrainer";
import { logDailyDrill } from "../../store/actions/usersActions";
import "../Counting/CountingTrumpsTrainer.css";
import "./TreadmillPracticePage.css";
import { colourSuits } from "../UI/SuitText";

const NARROW_MAX_PX = 480;
const TOOL_KEYS = {
  HAND_SHAPE: "hand-shape",
  OPPONENT_SHAPE: "opponent-shape",
  BUILDING_BLOCKS: "building-blocks",
  CARD_RUSH: "card-rush",
  COUNT_REAL_HANDS: "count-real-hands",
  BRIDGE_SUDOKU: "bridge-sudoku",
};

/** Bridge Sudoku is being built (Paul, 27 Oct 2026): its tab shows on localhost only until he says publish. */
const BRIDGE_SUDOKU_LIVE = true; // published 28 Oct 2026 (Paul: "publish it")
const showBridgeSudoku = () => BRIDGE_SUDOKU_LIVE || (typeof window !== "undefined" && /^(localhost|127.0.0.1)$/.test(window.location.hostname));

/** Flip to true when Card Rush is ready to go public. */
const CARD_RUSH_PUBLIC = true;

/** Guests only. Since the front-door review (2026-09-04) a free account plays
    level 1 of every drill without a daily cap, so the old "4 free correct
    answers a day" note has no one left to show to. */
const TREADMILL_GUEST_ACCOUNT_PROMO_NOTE =
  "Create a free account below - level 1 is unlimited. Basic and Premium open every level.";

const TREADMILL_EMBEDDED_SIGNUP_TITLE = "Create a username to play";
const TREADMILL_EMBEDDED_SIGNUP_SUBTITLE = "30 second sign up, then you're ready to play!";

const TREADMILL_GUEST_HAND_SHAPE_NOTE =
  "Create a free account below for 4 free correct answers a day. Basic and Premium are unlimited.";

function treadmillLockedPreviewNote({ uid, tool }) {
  if (!uid) return tool === "hand-shape" ? TREADMILL_GUEST_HAND_SHAPE_NOTE : TREADMILL_GUEST_ACCOUNT_PROMO_NOTE;
  return undefined;
}

// Points for each Game Plan daily drill - KEEP THE SAME as POINTS.shape10 / POINTS.opp10 in content-app/lib/thisMonth.js.
const DRILL_PTS = { shape10: 3, opp10: 3, sudoku5: 5, count1: 1, count2: 2 };
const DRILL_NAMES = { shape10: "Hand shape", opp10: "Opponent shape", sudoku5: "Bridge Sudoku", count1: "Count real hands level 1", count2: "Count real hands level 2" };

function toolFromPathname(pathname) {
  const p = pathname || "";
  if (p.endsWith("/opponent-shape")) return TOOL_KEYS.OPPONENT_SHAPE;
  if (p.endsWith("/building-blocks")) return TOOL_KEYS.BUILDING_BLOCKS;
  if (p.endsWith("/card-rush")) return TOOL_KEYS.CARD_RUSH;
  if (p.endsWith("/count-real-hands")) return TOOL_KEYS.COUNT_REAL_HANDS;
  if (p.endsWith("/bridge-sudoku") && showBridgeSudoku()) return TOOL_KEYS.BRIDGE_SUDOKU;
  return TOOL_KEYS.HAND_SHAPE;
}

function pathnameForTool(tool) {
  if (tool === TOOL_KEYS.OPPONENT_SHAPE) return "/treadmill/practice/opponent-shape";
  if (tool === TOOL_KEYS.BUILDING_BLOCKS) return "/treadmill/practice/building-blocks";
  if (tool === TOOL_KEYS.CARD_RUSH) return "/treadmill/practice/card-rush";
  if (tool === TOOL_KEYS.COUNT_REAL_HANDS) return "/treadmill/practice/count-real-hands";
  if (tool === TOOL_KEYS.BRIDGE_SUDOKU) return "/treadmill/practice/bridge-sudoku";
  return "/treadmill/practice";
}

/** GA4 event param `treadmill_tool` — snake_case per GA4 conventions. */
function treadmillToolGaValue(tool) {
  if (tool === TOOL_KEYS.OPPONENT_SHAPE) return "opponent_shape";
  if (tool === TOOL_KEYS.BUILDING_BLOCKS) return "building_blocks";
  if (tool === TOOL_KEYS.CARD_RUSH) return "card_rush";
  if (tool === TOOL_KEYS.COUNT_REAL_HANDS) return "count_real_hands";
  if (tool === TOOL_KEYS.BRIDGE_SUDOKU) return "bridge_sudoku";
  return "hand_shape";
}

function TreadmillPracticePage({
  history,
  uid,
  authReady,
  subscriptionActive,
  isAdmin,
  firstName,
  surname,
  startFacebookLogin: doFacebookLogin,
  startGoogleLogin: doGoogleLogin,
  signupEmailAndPasswordLogin: doSignupEmail,
  setProfileName: doSetProfileName,
  logDrill,
  subscriptionKnown,
}) {
  const practiceViewSentRef = useRef(false);
  const treadmillToolGaSentRef = useRef(null);
  const [activeTool, setActiveTool] = useState(() => toolFromPathname(history.location?.pathname));
  const [isNarrowViewport, setIsNarrowViewport] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(`(max-width: ${NARROW_MAX_PX}px)`).matches : false
  );

  useEffect(() => {
    if (practiceViewSentRef.current) return;
    practiceViewSentRef.current = true;
    sendPracticeEvent("practice_view", {
      trainer: "Treadmill",
      category_key: "treadmill",
      treadmill_tool: treadmillToolGaValue(toolFromPathname(history.location.pathname)),
    });
  }, [history.location.pathname]);

  useEffect(() => {
    setActiveTool(toolFromPathname(history.location?.pathname));
  }, [history.location?.pathname]);

  const cardRushPreviewPuzzleId = useMemo(() => {
    const search = history.location?.search || "";
    const params = new URLSearchParams(search);
    return params.get("cardRushPreview") || "";
  }, [history.location?.search]);

  useEffect(() => {
    if (treadmillToolGaSentRef.current === activeTool) return;
    treadmillToolGaSentRef.current = activeTool;
    sendTreadmillEvent("treadmill_tool_select", {
      treadmill_tool: treadmillToolGaValue(activeTool),
      auth_segment: treadmillAuthSegmentForGa({
        uid,
        subscriptionActive,
        isAdmin,
      }),
    });
  }, [activeTool, uid, subscriptionActive, isAdmin]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia(`(max-width: ${NARROW_MAX_PX}px)`);
    const update = () => setIsNarrowViewport(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const isLocalhost =
    typeof window !== "undefined" && /localhost|127\.0\.0\.1/.test(window.location?.hostname || "");

  const showGuestSignup = authReady && !uid;
  // localhost is never members-gated (same rule as guided bidding) so Paul can test every level locally (9 Oct 2026).
  const treadmillUnlimited = isAdmin || !!subscriptionActive || isLocalhost;
  // Front-door review (2026-09-04): a free account gets level 1 unlimited, so
  // the daily cap no longer applies to anyone who is signed in. Levels 2+ of
  // Count Real Hands still key off `treadmillUnlimited` (members only). The
  // daily-count plumbing is kept keyed on `dailyUnlimited` so the cap can be
  // brought back for a tier by changing this one line.
  const dailyUnlimited = treadmillUnlimited || !!uid;
  // HAND SHAPE is the exception (Paul, 20 Sep 2026: "for hand shape tool, shouldn't be unlimited"): a free
  // account is back to 4 free correct answers a day on it; Basic and Premium are unlimited. It is a Game
  // Plan points drill now (10 in a row), so it is a members' tool with a daily taste for everyone else.
  const handUnlimited = treadmillUnlimited;
  const [dailyTick, setDailyTick] = useState(0);

  const consumeDaily = useCallback(
    (trainerId) => {
      const unlimited = trainerId === TREADMILL_TRAINER_HAND_SHAPE ? handUnlimited : dailyUnlimited;
      if (!uid || unlimited) return;
      incrementTreadmillDailyCount(uid, trainerId);
      setDailyTick((t) => t + 1);
    },
    [uid, dailyUnlimited, handUnlimited]
  );

  const opponentDailyExhausted = useMemo(
    () =>
      !!uid &&
      !dailyUnlimited &&
      treadmillDailyLimitReached(uid, TREADMILL_TRAINER_OPPONENT_SHAPE, dailyUnlimited),
    [uid, dailyUnlimited, dailyTick]
  );
  const buildingDailyExhausted = useMemo(
    () =>
      !!uid &&
      !dailyUnlimited &&
      treadmillDailyLimitReached(uid, TREADMILL_TRAINER_BUILDING_BLOCKS, dailyUnlimited),
    [uid, dailyUnlimited, dailyTick]
  );
  const cardRushDailyExhausted = useMemo(
    () =>
      !!uid &&
      !dailyUnlimited &&
      treadmillDailyLimitReached(uid, TREADMILL_TRAINER_CARD_RUSH, dailyUnlimited),
    [uid, dailyUnlimited, dailyTick]
  );
  const cardRushFreeRemaining = useMemo(
    () => treadmillFreeRoundsRemaining(uid, TREADMILL_TRAINER_CARD_RUSH, dailyUnlimited),
    [uid, dailyUnlimited, dailyTick]
  );
  const handDailyBlocked = useMemo(
    () =>
      !!uid &&
      !handUnlimited &&
      treadmillDailyLimitReached(uid, TREADMILL_TRAINER_HAND_SHAPE, handUnlimited),
    [uid, handUnlimited, dailyTick]
  );

  const oppLocked = !uid || opponentDailyExhausted;
  const bbLocked = !uid || buildingDailyExhausted;
  const crLocked = !uid;
  const crAlias = useMemo(() => {
    const f = String(firstName || "").trim();
    const s = String(surname || "").trim();
    if (!f && !s) return "";
    if (!s) return f;
    if (!f) return s;
    return `${f} ${s.charAt(0)}.`;
  }, [firstName, surname]);

  const handFreeRemaining = useMemo(
    () => treadmillFreeRoundsRemaining(uid, TREADMILL_TRAINER_HAND_SHAPE, handUnlimited),
    [uid, handUnlimited, dailyTick]
  );

  const loginReturnPath = `${history.location.pathname || "/treadmill/practice"}${
    history.location.search || ""
  }`;

  // Opened inside the Game Plan page's panel: just the tool, no category or tool tabs.
  const framed = typeof window !== "undefined" && window.self !== window.top;
  // Someone who is (or was last) signed in: wait quietly until we know who they are AND what they
  // pay for, instead of flashing the locked / upgrade state for a second. Guests and crawlers have no
  // hint, so they see the page at once, exactly as before. Gives up after 6 seconds either way.
  let memberHint = framed;
  try { if (localStorage.getItem("bc_nav_member_hint") === "1") memberHint = true; } catch (e) {}
  const [waitedTooLong, setWaitedTooLong] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setWaitedTooLong(true), 6000);
    return () => window.clearTimeout(t);
  }, []);
  const settling = memberHint && !waitedTooLong && (!authReady || (!!uid && !subscriptionKnown));

  // Game Plan daily points (Paul, 20 Oct 2026): "+3 points" the moment a drill's 10th right answer lands, once a
  // day; afterwards a quieter "already got today's points". Inside the Game Plan panel (an iframe) the Game Plan page
  // shows its own message, so this one stays hidden there.
  const [drillToast, setDrillToast] = useState(null); // { text, fresh }
  const earnDrill = useCallback((key) => {
    const p = logDrill(uid, key);
    if (!p || typeof p.then !== "function") return undefined;
    // Returns the save's result too, so a tool can show the points in its own panel (Bridge Sudoku, 28 Oct 2026).
    return p.then((res) => {
      if (!res || framed) return res;
      const n = DRILL_PTS[key] || 0;
      setDrillToast(res.fresh
        ? { fresh: true, text: `+${n} ${n === 1 ? "point" : "points"}! Today's ${DRILL_NAMES[key] || "Counting"} points are on your Game Plan.` }
        : { fresh: false, text: "Well done! You already have today's points for this one." });
      return res;
    });
  }, [logDrill, uid, framed]);
  useEffect(() => {
    if (!drillToast) return undefined;
    const t = window.setTimeout(() => setDrillToast(null), 6000);
    return () => window.clearTimeout(t);
  }, [drillToast]);

  return (
    <div
      className={`ct-page ${isLocalhost ? "ct-page--localhost" : ""} ${isNarrowViewport ? "tm-page--narrow" : ""} ${framed ? "tm-page--framed" : ""}`}
    >
      <div className="ct-layout">
        <div className="ct-stage">
          <div className="ct-topNavWrap">
            <div className="ct-topNav" aria-label="Treadmill navigation">
              <div className="ct-categoryRow" aria-label="Trainer category">
                <div className="ct-categoryTabs" role="tablist">
                  {TRAINER_CATEGORY_TABS.map((c) => (
                    <Link
                      key={c.key}
                      to={c.path}
                      className={`ct-categoryTab ${c.key === "treadmill" ? "ct-categoryTab--active" : ""}`}
                      role="tab"
                      aria-selected={c.key === "treadmill"}
                    >
                      {colourSuits(c.label)}
                      {c.new && (
                        <span className="ct-newBadge" aria-label="New">
                          New
                        </span>
                      )}
                    </Link>
                  ))}
                </div>
              </div>

              <div className="ct-topNavRow ct-topNavRow--diff" aria-label="Treadmill tools">
                <span className="ct-topNavLabel">Tool</span>
                <div className="ct-diffTabs" role="tablist">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeTool === TOOL_KEYS.HAND_SHAPE}
                    className={`ct-diffTab tm-toolTabBtn ${
                      activeTool === TOOL_KEYS.HAND_SHAPE ? "ct-diffTab--active" : ""
                    }`}
                    onClick={() => {
                      const path = pathnameForTool(TOOL_KEYS.HAND_SHAPE);
                      if (history.location.pathname !== path) history.replace(path);
                    }}
                  >
                    Hand shape {!uid ? "🔒" : ""}
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeTool === TOOL_KEYS.OPPONENT_SHAPE}
                    className={`ct-diffTab tm-toolTabBtn ${
                      activeTool === TOOL_KEYS.OPPONENT_SHAPE ? "ct-diffTab--active" : ""
                    }`}
                    onClick={() => {
                      const path = pathnameForTool(TOOL_KEYS.OPPONENT_SHAPE);
                      if (history.location.pathname !== path) history.replace(path);
                    }}
                  >
                    Opponent shape {!uid ? "🔒" : ""}
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeTool === TOOL_KEYS.BUILDING_BLOCKS}
                    className={`ct-diffTab tm-toolTabBtn ${
                      activeTool === TOOL_KEYS.BUILDING_BLOCKS ? "ct-diffTab--active" : ""
                    }`}
                    onClick={() => {
                      const path = pathnameForTool(TOOL_KEYS.BUILDING_BLOCKS);
                      if (history.location.pathname !== path) history.replace(path);
                    }}
                  >
                    Building blocks {!uid ? "🔒" : ""}
                  </button>
                  {(CARD_RUSH_PUBLIC || !!uid) ? (
                    <button
                      type="button"
                      role="tab"
                      aria-selected={activeTool === TOOL_KEYS.CARD_RUSH}
                      className={`ct-diffTab tm-toolTabBtn ${
                        activeTool === TOOL_KEYS.CARD_RUSH ? "ct-diffTab--active" : ""
                      }`}
                      onClick={() => {
                        const path = pathnameForTool(TOOL_KEYS.CARD_RUSH);
                        if (history.location.pathname !== path) history.replace(path);
                      }}
                    >
                      Card Rush
                      {!CARD_RUSH_PUBLIC ? (
                        <span className="ct-newBadge" style={{ background: "#64748b" }} aria-label="Preview">
                          Preview
                        </span>
                      ) : null}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeTool === TOOL_KEYS.COUNT_REAL_HANDS}
                    className={`ct-diffTab tm-toolTabBtn ${
                      activeTool === TOOL_KEYS.COUNT_REAL_HANDS ? "ct-diffTab--active" : ""
                    }`}
                    onClick={() => {
                      const path = pathnameForTool(TOOL_KEYS.COUNT_REAL_HANDS);
                      if (history.location.pathname !== path) history.replace(path);
                    }}
                  >
                    Count real hands
                  </button>
                  {showBridgeSudoku() ? (
                    <button
                      type="button"
                      role="tab"
                      aria-selected={activeTool === TOOL_KEYS.BRIDGE_SUDOKU}
                      className={`ct-diffTab tm-toolTabBtn ${
                        activeTool === TOOL_KEYS.BRIDGE_SUDOKU ? "ct-diffTab--active" : ""
                      }`}
                      onClick={() => {
                        const path = pathnameForTool(TOOL_KEYS.BRIDGE_SUDOKU);
                        if (history.location.pathname !== path) history.replace(path);
                      }}
                    >
                      Bridge Sudoku
                      {!BRIDGE_SUDOKU_LIVE ? (
                        <span className="ct-newBadge" style={{ background: "#64748b" }} aria-label="Preview">
                          Preview
                        </span>
                      ) : null}
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          {/* Count real hands deals FULL 13-card hands on the shared trainer's
              felt (~760px + its explanation sidebar) — the 42rem column that
              suits the little seeded tools crushes it, so it gets a wide main. */}
          <div className={activeTool === TOOL_KEYS.COUNT_REAL_HANDS || activeTool === TOOL_KEYS.BRIDGE_SUDOKU ? "tm-main tm-main--wide" : "tm-main"}>
            {settling ? (
              <p className="tm-settling" role="status">Loading…</p>
            ) : activeTool === TOOL_KEYS.BRIDGE_SUDOKU ? (
              // Game Plan daily (Paul, 28 Oct 2026): 3 right in a day = 5 points, members.
              <>
                {/* Members only, with ONE free puzzle a day for a free account and a sign-up prompt for guests (Paul, 28 Oct
                    2026: "make this members only by the way, allow non members to do 1 a day if they have an account").
                    On localhost ?as=free / ?as=guest previews those views (localhost is otherwise never gated). */}
                <BridgeSudokuTrainer
                  uid={uid || ""}
                  access={(() => {
                    const as = isLocalhost ? new URLSearchParams(history.location.search).get("as") : null;
                    if (as === "guest" || as === "free") return as;
                    if (isLocalhost) return "member"; // localhost is never gated, signed in or not (Paul previews here)
                    return !uid ? "guest" : treadmillUnlimited ? "member" : "free";
                  })()}
                  onSubscribe={() => history.push("/membership")}
                  onFiveRight={uid && treadmillUnlimited ? () => earnDrill("sudoku5") : undefined}
                />
                {showGuestSignup && (!isLocalhost || new URLSearchParams(history.location.search).get("as") === "guest") ? (
                  <section className="tm-signupPanel" aria-label="Create account">
                    <Signup
                      embedded
                      embeddedTitle={TREADMILL_EMBEDDED_SIGNUP_TITLE}
                      embeddedSubtitle={TREADMILL_EMBEDDED_SIGNUP_SUBTITLE}
                      facebookLogin={doFacebookLogin}
                      googleLogin={doGoogleLogin}
                      emailLogin={doSignupEmail}
                      setProfileName={doSetProfileName}
                      history={history}
                      redirectPathAfterAuth={loginReturnPath}
                    />
                  </section>
                ) : null}
              </>
            ) : activeTool === TOOL_KEYS.COUNT_REAL_HANDS ? (
              <>
                {/* Paul's own words on why the tool is worth doing (31 Aug 2026).
                    Replaces nothing - the page had no intro line at all. */}
                <p className="tm-toolIntro">
                  Counting suits and counting full hands is not only one of the most
                  satisfying skills in bridge, but it is also one of the most
                  important. Additionally - it is great for your mind and mental
                  health!
                </p>
                {/* Endless generated deals. Level 1 is open to everyone; levels 2
                    and 3 need a subscription. `hideCategoryTabs` stops the shared
                    trainer drawing a second copy of the category tier this page
                    already renders above. */}
                <CountingHandsTrainer
                  history={history}
                  location={history.location}
                  hideCategoryTabs
                  canUseAllModes={treadmillUnlimited}
                  /* Game Plan daily: levels 1 and 2 only for now, 1 and 2 points (Paul, 20 Oct 2026). */
                  onHandDone={uid && treadmillUnlimited ? (mode) => { if (Number(mode) === 1 || Number(mode) === 2) earnDrill(`count${mode}`); } : undefined}
                  onSubscribeClick={() => history.push("/membership")}
                />
                {!uid ? (
                  <section className="tm-signupPanel" aria-label="Create account">
                    <Signup
                      embedded
                      embeddedTitle={TREADMILL_EMBEDDED_SIGNUP_TITLE}
                      embeddedSubtitle={TREADMILL_EMBEDDED_SIGNUP_SUBTITLE}
                      facebookLogin={doFacebookLogin}
                      googleLogin={doGoogleLogin}
                      emailLogin={doSignupEmail}
                      setProfileName={doSetProfileName}
                      history={history}
                      redirectPathAfterAuth={loginReturnPath}
                    />
                  </section>
                ) : null}
              </>
            ) : activeTool === TOOL_KEYS.CARD_RUSH ? (
              <>
                <CardRushTrainer
                  uid={uid || ""}
                  alias={crAlias}
                  canRecordLeaderboard={authReady && !!uid}
                  previewPuzzleId={cardRushPreviewPuzzleId}
                  lockedPreview={crLocked}
                  previewNote={treadmillLockedPreviewNote({ uid })}
                  dailyInteractionBlocked={!!uid && !dailyUnlimited && cardRushDailyExhausted}
                  dailyFreeRemaining={cardRushFreeRemaining}
                  onDailyRoundConsumed={
                    uid && !dailyUnlimited
                      ? () => consumeDaily(TREADMILL_TRAINER_CARD_RUSH)
                      : undefined
                  }
                  onSubscribeClick={() => history.push("/membership")}
                  belowLeaderboardSlot={
                    showGuestSignup ? (
                      <section className="tm-signupPanel" aria-label="Create account">
                        <Signup
                          embedded
                          embeddedTitle={TREADMILL_EMBEDDED_SIGNUP_TITLE}
                          embeddedSubtitle={TREADMILL_EMBEDDED_SIGNUP_SUBTITLE}
                          facebookLogin={doFacebookLogin}
                          googleLogin={doGoogleLogin}
                          emailLogin={doSignupEmail}
                          setProfileName={doSetProfileName}
                          history={history}
                          redirectPathAfterAuth={loginReturnPath}
                        />
                      </section>
                    ) : null
                  }
                />
                {!uid ? (
                  <section className="tm-signupPanel" aria-label="Create account">
                    <Signup
                      embedded
                      embeddedTitle={TREADMILL_EMBEDDED_SIGNUP_TITLE}
                      embeddedSubtitle={TREADMILL_EMBEDDED_SIGNUP_SUBTITLE}
                      facebookLogin={doFacebookLogin}
                      googleLogin={doGoogleLogin}
                      emailLogin={doSignupEmail}
                      setProfileName={doSetProfileName}
                      history={history}
                      redirectPathAfterAuth={loginReturnPath}
                    />
                  </section>
                ) : null}
              </>
            ) : activeTool === TOOL_KEYS.BUILDING_BLOCKS ? (
              <>
                <BuildingBlocksTrainer
                  lockedPreview={bbLocked}
                  previewNote={treadmillLockedPreviewNote({ uid })}
                  onDailyRoundConsumed={
                    uid && !dailyUnlimited
                      ? () => consumeDaily(TREADMILL_TRAINER_BUILDING_BLOCKS)
                      : undefined
                  }
                />
                {!uid ? (
                  <section className="tm-signupPanel" aria-label="Create account">
                    <Signup
                      embedded
                      embeddedTitle={TREADMILL_EMBEDDED_SIGNUP_TITLE}
                      embeddedSubtitle={TREADMILL_EMBEDDED_SIGNUP_SUBTITLE}
                      facebookLogin={doFacebookLogin}
                      googleLogin={doGoogleLogin}
                      emailLogin={doSignupEmail}
                      setProfileName={doSetProfileName}
                      history={history}
                      redirectPathAfterAuth={loginReturnPath}
                    />
                  </section>
                ) : null}
              </>
            ) : activeTool === TOOL_KEYS.HAND_SHAPE ? (
              <HandShapeMissingClubTrainer
                uid={uid || ""}
                treadmillUnlimited={handUnlimited}
                lockedPreview={!uid}
                previewNote={treadmillLockedPreviewNote({ uid, tool: "hand-shape" })}
                dailyInteractionBlocked={handDailyBlocked}
                dailyFreeRemaining={handFreeRemaining}
                onDailyRoundConsumed={() => consumeDaily(TREADMILL_TRAINER_HAND_SHAPE)}
                // Game Plan daily (Paul, 20 Oct 2026): 10 right in a session, not 10 in a row.
                onTenRight={uid && treadmillUnlimited ? () => earnDrill("shape10") : undefined}
                onSubscribeClick={() => history.push("/membership")}
                canRecordLeaderboard={authReady && !!uid}
                belowLeaderboardSlot={
                  showGuestSignup ? (
                    <section className="tm-signupPanel" aria-label="Create account">
                      <Signup
                        embedded
                        embeddedTitle={TREADMILL_EMBEDDED_SIGNUP_TITLE}
                        embeddedSubtitle={TREADMILL_EMBEDDED_SIGNUP_SUBTITLE}
                        facebookLogin={doFacebookLogin}
                        googleLogin={doGoogleLogin}
                        emailLogin={doSignupEmail}
                        setProfileName={doSetProfileName}
                        history={history}
                        redirectPathAfterAuth={loginReturnPath}
                      />
                    </section>
                  ) : null
                }
              />
            ) : (
              <>
                <OpponentShapeTrainer
                  lockedPreview={oppLocked}
                  onTenRight={uid && treadmillUnlimited ? () => earnDrill("opp10") : undefined}
                  previewNote={treadmillLockedPreviewNote({ uid })}
                  onDailyRoundConsumed={
                    uid && !dailyUnlimited
                      ? () => consumeDaily(TREADMILL_TRAINER_OPPONENT_SHAPE)
                      : undefined
                  }
                />
                {!uid ? (
                  <section className="tm-signupPanel" aria-label="Create account">
                    <Signup
                      embedded
                      embeddedTitle={TREADMILL_EMBEDDED_SIGNUP_TITLE}
                      embeddedSubtitle={TREADMILL_EMBEDDED_SIGNUP_SUBTITLE}
                      facebookLogin={doFacebookLogin}
                      googleLogin={doGoogleLogin}
                      emailLogin={doSignupEmail}
                      setProfileName={doSetProfileName}
                      history={history}
                      redirectPathAfterAuth={loginReturnPath}
                    />
                  </section>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>

      {drillToast ? (
        <div className={`tm-drillToast${drillToast.fresh ? "" : " tm-drillToast--plain"}`} role="status" aria-live="polite">
          {drillToast.text}
        </div>
      ) : null}

      {isLocalhost && (
        <div className="tm-viewportHint" aria-hidden="true">
          {isNarrowViewport ? `Phone-width (≤${NARROW_MAX_PX}px)` : `Desktop width (>${NARROW_MAX_PX}px)`}
        </div>
      )}
    </div>
  );
}

const mapStateToProps = (state) => ({
  uid: state.auth?.uid,
  authReady: state.auth?.authReady === true,
  subscriptionActive: state.auth?.subscriptionActive === true,
  subscriptionKnown: state.auth?.subscriptionKnown === true,
  isAdmin: state.auth?.a === true,
  firstName: state.auth?.firstName || "",
  surname: state.auth?.surname || "",
});

const mapDispatchToProps = (dispatch) => ({
  startFacebookLogin: () => dispatch(startFacebookLogin()),
  startGoogleLogin: () => dispatch(startGoogleLogin()),
  signupEmailAndPasswordLogin: (email, password) => dispatch(signupEmailAndPasswordLogin(email, password)),
  setProfileName: (firstName, surname) => dispatch(setProfileName(firstName, surname)),
  logDrill: (uid, key) => dispatch(logDailyDrill(uid, key)),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(TreadmillPracticePage));
